import { createStore, useStore } from "zustand"
import { useStoreWithEqualityFn } from "zustand/traditional"
import { TreatmentPlan } from "@/modules/patients/detail/progress-note/components/progress-note-editable/treatment-plan-section"
import { ProgressNoteForm } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import { ProgressNoteAndParentData } from "@/modules/patients/detail/progress-note/progress-note-card"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import {
  CarePlanSummaryDocument,
  CH_PROGRESS_NOTE_DOCUMENT_TYPES,
  ChDocumentType,
  ChProgressNoteDocument,
  DE_PROGRESS_NOTE_DOCUMENT_TYPES,
  DeDocumentType,
  DeProgressNoteDocument,
  GenericDocument,
  MedicalReportAttachment,
  ProgressNoteDocumentType,
} from "@/modules/patients/types/ProgressNoteDocument"
import { Country } from "@/types/__generated__/union-types"

const getDocumentsByCountryFragment = (progressNote: ProgressNoteData): GenericDocument[] => {
  switch (progressNote.__typename) {
    case "ch_ProgressNote": {
      return CH_PROGRESS_NOTE_DOCUMENT_TYPES.reduce<GenericDocument[]>((acc, type) => {
        if (type === "carePlanSummary" || !progressNote[type]) return acc
        else if (type === "medicalReportAttachments") {
          const attachment = progressNote[type]?.edges?.at(0)
          if (attachment) {
            acc.push({ id: attachment.node.id, signedBy: null, type, concludedBy: null })
          } else {
            return acc
          }
        } else {
          const { id, signedBy, concludedBy } = progressNote[type] ?? {}
          if (id) {
            acc.push({
              id,
              signedBy: signedBy ?? null,
              type,
              concludedBy: concludedBy ?? null,
            })
          }
        }

        return acc
      }, [])
    }
    case "de_ProgressNote": {
      return DE_PROGRESS_NOTE_DOCUMENT_TYPES.reduce<GenericDocument[]>((acc, type) => {
        if (type === "carePlanSummary" || !progressNote[type]) return acc
        else if (type === "medicalReportAttachments") {
          const attachment = progressNote[type]?.edges?.at(0)
          if (attachment) {
            acc.push({ id: attachment.node.id, signedBy: null, type, concludedBy: null })
          } else {
            return acc
          }
        } else {
          const { id, signedBy, concludedBy } = progressNote[type] ?? {}
          if (id) {
            acc.push({
              id,
              signedBy: signedBy ?? null,
              type,
              concludedBy: concludedBy ?? null,
            })
          }
        }

        return acc
      }, [])
    }
    default:
      return []
  }
}

const getUnsignedDocuments = (progressNote: ProgressNoteData): GenericDocument[] => {
  return getDocumentsByCountryFragment(progressNote).filter((document) => !document.signedBy)
}

const formatProgressNoteDocuments = (progressNote: ProgressNoteData, excludeConcluded: boolean) => {
  switch (progressNote.__typename) {
    case "ch_ProgressNote": {
      const {
        prescription,
        sickLeave,
        attestationGuardian,
        physiotherapy,
        ch_nutritionalPrescription,
        psychologicalPsychotherapy,
        generalAttestation,
        referralLetter,
        treatmentPlan,
        carePlanSummary,
        measLaboratoryPrescription,
        ch_measRadiologyPrescription,
        medicalReportAttachments,
        ch_neuropsychologicDiagnosticPrescription,
        ch_laboratoryPrescription,
        ch_consultationReport,
      } = progressNote

      const carePlanSummaryNode: Partial<CarePlanSummaryDocument> = carePlanSummary?.edges?.[0]?.node
        ? { ...carePlanSummary?.edges?.[0]?.node }
        : {}

      const chFormattedDocument: ChProgressNoteDocument = {
        carePlanSummary: getCarePlanSummary(
          {
            progressNoteId: progressNote.id,
            treatmentPlan,
            carePlanSummary: carePlanSummaryNode,
            type: "carePlanSummary",
          },
          excludeConcluded,
        ),
        prescription: getDocument({ document: prescription, type: "prescription" }, excludeConcluded),
        sickLeave: getDocument({ document: sickLeave, type: "sickLeave" }, excludeConcluded),
        attestationGuardian: getDocument(
          { document: attestationGuardian, type: "attestationGuardian" },
          excludeConcluded,
        ),
        physiotherapy: getDocument({ document: physiotherapy, type: "physiotherapy" }, excludeConcluded),
        ch_nutritionalPrescription: getDocument(
          {
            document: ch_nutritionalPrescription,
            type: "ch_nutritionalPrescription",
          },
          excludeConcluded,
        ),
        psychologicalPsychotherapy: getDocument(
          {
            document: psychologicalPsychotherapy,
            type: "psychologicalPsychotherapy",
          },
          excludeConcluded,
        ),
        generalAttestation: getDocument({ document: generalAttestation, type: "generalAttestation" }, excludeConcluded),
        referralLetter: getDocument({ document: referralLetter, type: "referralLetter" }, excludeConcluded),
        measLaboratoryPrescription: getDocument(
          { document: measLaboratoryPrescription, type: "measLaboratoryPrescription" },
          excludeConcluded,
        ),
        ch_measRadiologyPrescription: getDocument(
          { document: ch_measRadiologyPrescription, type: "ch_measRadiologyPrescription" },
          excludeConcluded,
        ),
        medicalReportAttachments: medicalReportAttachments?.edges?.at(0)
          ? { type: "medicalReportAttachments", attachments: medicalReportAttachments }
          : null,
        ch_neuropsychologicDiagnosticPrescription: getDocument(
          { document: ch_neuropsychologicDiagnosticPrescription, type: "ch_neuropsychologicDiagnosticPrescription" },
          excludeConcluded,
        ),
        ch_laboratoryPrescription: getDocument(
          { document: ch_laboratoryPrescription, type: "ch_laboratoryPrescription" },
          excludeConcluded,
        ),
        ch_consultationReport: getDocument(
          { document: ch_consultationReport, type: "ch_consultationReport" },
          excludeConcluded,
        ),
      }

      return chFormattedDocument
    }

    case "de_ProgressNote": {
      const {
        attestationGuardian,
        sickLeave,
        therapyPrescription,
        generalAttestation,
        referralLetter,
        treatmentPlan,
        carePlanSummary,
        prescription,
        medicalReportAttachments,
      } = progressNote

      const carePlanSummaryNode: Partial<CarePlanSummaryDocument> = carePlanSummary?.edges?.[0]?.node
        ? { ...carePlanSummary?.edges?.[0]?.node }
        : {}

      const deFormattedDocument: DeProgressNoteDocument = {
        carePlanSummary: getCarePlanSummary(
          {
            progressNoteId: progressNote.id,
            treatmentPlan,
            carePlanSummary: carePlanSummaryNode,
            type: "carePlanSummary",
          },
          excludeConcluded,
        ),
        prescription: getDocument({ document: prescription, type: "prescription" }, excludeConcluded),
        attestationGuardian: getDocument(
          { document: attestationGuardian, type: "attestationGuardian" },
          excludeConcluded,
        ),
        sickLeave: getDocument({ document: sickLeave, type: "sickLeave" }, excludeConcluded),
        therapyPrescription: getDocument(
          { document: therapyPrescription, type: "therapyPrescription" },
          excludeConcluded,
        ),
        generalAttestation: getDocument({ document: generalAttestation, type: "generalAttestation" }, excludeConcluded),
        referralLetter: getDocument({ document: referralLetter, type: "referralLetter" }, excludeConcluded),
        medicalReportAttachments: medicalReportAttachments?.edges?.at(0)
          ? { type: "medicalReportAttachments", attachments: medicalReportAttachments }
          : null,
      }
      return deFormattedDocument
    }
  }
}

const getCarePlanSummary = (
  {
    progressNoteId,
    treatmentPlan,
    carePlanSummary,
    type,
  }: {
    progressNoteId: string
    treatmentPlan: TreatmentPlan | null
    carePlanSummary: Partial<CarePlanSummaryDocument>
    type: ChDocumentType | DeDocumentType
  },
  excludeConcluded: boolean,
) => {
  if (treatmentPlan === null || (excludeConcluded && carePlanSummary.concludedBy)) return null

  return {
    ...carePlanSummary,
    id: carePlanSummary?.id || getCarePlanSummaryDocumentId(progressNoteId),
    progressNoteId,
    type,
  }
}

const getDocument = <T extends { concludedBy: string | null | undefined }>(
  {
    document,
    type,
  }: {
    document: T | null
    type: ChDocumentType | DeDocumentType
  },
  excludeConcluded: boolean,
) => {
  if (document === null || (excludeConcluded && document.concludedBy)) {
    return null
  }

  return { ...document, type }
}

type SelectedDocumentForViewingType = {
  type: ProgressNoteDocumentType
  fileId?: string | null
  signedAt?: string | null
  signedBy?: string | null
  sentAt?: string | null
  sentBy?: string | null
}

type State = {
  previewProgressNotes: ProgressNoteAndParentData[]
  countryCode: Country | null
  chFormattedProgressNotes: Map<string, ChProgressNoteDocument> | null
  deFormattedProgressNotes: Map<string, DeProgressNoteDocument> | null
  chFormattedProgressNotesWithAppointmentInfo: Map<
    string,
    { documents: ChProgressNoteDocument; appointmentId: string }
  > | null
  deFormattedProgressNotesWithAppointmentInfo: Map<
    string,
    { documents: DeProgressNoteDocument; appointmentId: string }
  > | null
  selectedDocumentForViewing: SelectedDocumentForViewingType | null
  selectedMedicalReportForViewing: MedicalReportAttachment | null
}

type Actions = {
  actions: {
    initializeProgressNoteDocuments: (
      progressNotesData: ProgressNoteAndParentData[],
      progressNoteForms: Map<string, ProgressNoteForm>,
      country: Country,
    ) => void
    setSelectedDocumentForViewing: (document: SelectedDocumentForViewingType) => void
    updateProgressNoteCarePlanSummaryDocument: (
      country: Country,
      progressNoteId: string,
      carePlanSummaryDocument: CarePlanSummaryDocument,
    ) => void
    setMedicalReportDocumentForViewing: (attachment: MedicalReportAttachment) => void
  }
}

type ExtractState<S> = S extends {
  getState: () => infer T
}
  ? T
  : never

type Params<U> = Parameters<typeof useStore<typeof documentStore, U>>

const initialState: State = {
  previewProgressNotes: [],
  chFormattedProgressNotes: null,
  deFormattedProgressNotes: null,
  chFormattedProgressNotesWithAppointmentInfo: null,
  deFormattedProgressNotesWithAppointmentInfo: null,
  countryCode: null,
  selectedDocumentForViewing: null,
  selectedMedicalReportForViewing: null,
}

const documentStore = createStore<State & Actions>((set, get) => {
  return {
    ...initialState,

    actions: {
      async initializeProgressNoteDocuments(progressNotesData, progressNoteForms, country) {
        const filteredData = progressNotesData.filter((item) =>
          progressNoteForms.has(item.episodeOfCareProgressNoteData.id),
        )

        const chProgressNoteWithAppointmentInfoMap = new Map<
          string,
          { documents: ChProgressNoteDocument; appointmentId: string }
        >()
        const deProgressNoteWithAppointmentInfoMap = new Map<
          string,
          { documents: DeProgressNoteDocument; appointmentId: string }
        >()

        filteredData.forEach((item) => {
          switch (item.episodeOfCareProgressNoteData.__typename) {
            case "ch_ProgressNote": {
              const { episodeOfCareProgressNoteData: progressNote } = item
              const chFormattedDocument = formatProgressNoteDocuments(progressNote, true) as ChProgressNoteDocument
              chProgressNoteWithAppointmentInfoMap.set(progressNote.id, {
                documents: chFormattedDocument,
                appointmentId: item.episodeOfCareProgressNoteData.appointment.id,
              })
              break
            }
            case "de_ProgressNote": {
              const { episodeOfCareProgressNoteData: progressNote } = item
              const deFormattedDocument = formatProgressNoteDocuments(progressNote, true) as DeProgressNoteDocument
              deProgressNoteWithAppointmentInfoMap.set(progressNote.id, {
                documents: deFormattedDocument,
                appointmentId: item.episodeOfCareProgressNoteData.appointment.id,
              })
              break
            }
          }
        })

        const chProgressNoteMap = new Map(
          Array.from(chProgressNoteWithAppointmentInfoMap.entries()).map(([key, entry]) => [key, entry.documents]),
        )
        const deProgressNoteMap = new Map(
          Array.from(deProgressNoteWithAppointmentInfoMap.entries()).map(([key, entry]) => [key, entry.documents]),
        )
        set({
          previewProgressNotes: filteredData,
          chFormattedProgressNotes: chProgressNoteMap,
          deFormattedProgressNotes: deProgressNoteMap,
          chFormattedProgressNotesWithAppointmentInfo: chProgressNoteWithAppointmentInfoMap,
          deFormattedProgressNotesWithAppointmentInfo: deProgressNoteWithAppointmentInfoMap,
          countryCode: country,
        })
      },
      setSelectedDocumentForViewing(document) {
        set({ selectedDocumentForViewing: document, selectedMedicalReportForViewing: null })
      },
      updateProgressNoteCarePlanSummaryDocument(country, progressNoteId, carePlanSummaryDocument) {
        switch (country) {
          case "CH": {
            const newProgressNoteDocument = get().chFormattedProgressNotes?.get(progressNoteId)

            if (newProgressNoteDocument?.carePlanSummary) {
              newProgressNoteDocument.carePlanSummary = {
                ...newProgressNoteDocument.carePlanSummary,
                ...carePlanSummaryDocument,
              }

              const newChFormattedProgressNotes = new Map(get().chFormattedProgressNotes)
              newChFormattedProgressNotes.set(progressNoteId, newProgressNoteDocument)

              set({ chFormattedProgressNotes: newChFormattedProgressNotes })
            }

            break
          }
          case "DE": {
            const newProgressNoteDocument = get().deFormattedProgressNotes?.get(progressNoteId)

            if (newProgressNoteDocument?.carePlanSummary) {
              newProgressNoteDocument.carePlanSummary = {
                ...newProgressNoteDocument.carePlanSummary,
                ...carePlanSummaryDocument,
              }

              const newChFormattedProgressNotes = new Map(get().deFormattedProgressNotes)
              newChFormattedProgressNotes.set(progressNoteId, newProgressNoteDocument)

              set({ deFormattedProgressNotes: newChFormattedProgressNotes })
            }
            break
          }
        }
      },
      setMedicalReportDocumentForViewing(attachment) {
        set({ selectedMedicalReportForViewing: attachment, selectedDocumentForViewing: null })
      },
    },
  }
})

function useDocumentStore<U>(selector: Params<U>[1], equalityFn?: (a: U, b: U) => boolean) {
  return useStoreWithEqualityFn(documentStore, selector, equalityFn)
}

// Selectors
const actionsSelector = (state: ExtractState<typeof documentStore>) => state.actions
const previewProgressNotesSelector = (state: ExtractState<typeof documentStore>) => state.previewProgressNotes
const previewProgressNotesByAppointmentSelector = (state: ExtractState<typeof documentStore>, appointmentId: string) =>
  state.previewProgressNotes.filter((i) => i.episodeOfCareProgressNoteData.appointment.id === appointmentId)
const chProgressNotesSelector = (state: ExtractState<typeof documentStore>) => state.chFormattedProgressNotes
const deProgressNotesSelector = (state: ExtractState<typeof documentStore>) => state.deFormattedProgressNotes

const chProgressNotesByAppointmentIdSelector = (state: ExtractState<typeof documentStore>, appointmentId: string) => {
  if (!state.chFormattedProgressNotesWithAppointmentInfo) return null

  const filteredEntries = Array.from(state.chFormattedProgressNotesWithAppointmentInfo.entries())
    .filter(([, entry]) => entry.appointmentId === appointmentId)
    .map<[string, ChProgressNoteDocument]>(([key, entry]) => [key, entry.documents])

  return new Map(filteredEntries)
}
const deProgressNotesByAppointmentIdSelector = (state: ExtractState<typeof documentStore>, appointmentId: string) => {
  if (!state.deFormattedProgressNotesWithAppointmentInfo) return null

  const filteredEntries = Array.from(state.deFormattedProgressNotesWithAppointmentInfo.entries())
    .filter(([, entry]) => entry.appointmentId === appointmentId)
    .map<[string, DeProgressNoteDocument]>(([key, entry]) => [key, entry.documents])

  return new Map(filteredEntries)
}

// Getters
export const getDocumentActions = () => actionsSelector(documentStore.getState())
export const getPreviewProgressNotes = () => previewProgressNotesSelector(documentStore.getState())
export const getPreviewProgressNotesByAppointmentId = (appointmentId: string | null) =>
  appointmentId ? previewProgressNotesByAppointmentSelector(documentStore.getState(), appointmentId) : []

export const getChProgressNotes = () => chProgressNotesSelector(documentStore.getState())
export const getDeProgressNotes = () => deProgressNotesSelector(documentStore.getState())

export const getChProgressNotesByAppointmentId = (appointmentId: string | null) =>
  appointmentId ? chProgressNotesByAppointmentIdSelector(documentStore.getState(), appointmentId) : null
export const getDeProgressNotesByAppointmentId = (appointmentId: string | null) =>
  appointmentId ? deProgressNotesByAppointmentIdSelector(documentStore.getState(), appointmentId) : null

// Hooks
export const useDocumentActions = () => useDocumentStore(actionsSelector)

export const getCarePlanSummaryDocumentId = (progressNoteId: string) => `${progressNoteId}-carePlanSummary`

export {
  formatProgressNoteDocuments,
  getDocumentsByCountryFragment,
  getUnsignedDocuments,
  useDocumentStore,
  type SelectedDocumentForViewingType,
}
